import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from 'src/constants';
import Modal from 'src/components/layouts/Modal/Modal';
import Image from 'src/components/ui/Image/Image';
import Icon from 'src/components/ui/Icon/Icon';
import { downloadAttachment } from 'src/services/utility';

export type GalleryImage = {
  id: number;
  name?: string;
};

type Props = {
  images: GalleryImage[];
  index: number;
  onIndexChange: (next: number) => void;
  isOpen: boolean;
  onClose: () => void;
};

export const ReturnImageGallery: React.FC<Props> = ({ images, index, onIndexChange, isOpen, onClose }) => {
  const { t } = useTranslation();
  const total = images.length;
  const current = images[index];
  const hasMultiple = total > 1;

  const goPrev = useCallback(() => {
    if (!hasMultiple) return;
    onIndexChange((index - 1 + total) % total);
  }, [hasMultiple, index, onIndexChange, total]);

  const goNext = useCallback(() => {
    if (!hasMultiple) return;
    onIndexChange((index + 1) % total);
  }, [hasMultiple, index, onIndexChange, total]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, goPrev, goNext]);

  if (!current) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex flex-col gap-12 p-16 max-w-[90vw]">
        <div className="flex items-center justify-between gap-12 pr-32">
          <span className="typo-text-14 text-faded break-all">{current.name || `attachment-${current.id}`}</span>
          {hasMultiple && (
            <span className="typo-text-12 text-faded shrink-0">
              {index + 1} / {total}
            </span>
          )}
        </div>
        <div className="relative flex items-center justify-center bg-surface-base rounded-base">
          {hasMultiple && (
            <button
              type="button"
              onClick={goPrev}
              aria-label={t('returns.previous_image')}
              className="absolute left-8 z-10 surface-elevated rounded-full p-8 hover:opacity-90">
              <Icon name="arrowLeft" width={20} height={20} />
            </button>
          )}
          <Image
            imageUrl={`${API_BASE_URL}portal/utility/attachments/${current.id}/view`}
            alt={current.name || `attachment-${current.id}`}
            className="max-h-[75vh] max-w-full object-contain"
            fallbackComponent={<Icon name="imageUpload" width={48} height={48} />}
          />
          {hasMultiple && (
            <button
              type="button"
              onClick={goNext}
              aria-label={t('returns.next_image')}
              className="absolute right-8 z-10 surface-elevated rounded-full p-8 hover:opacity-90">
              <Icon name="arrowRight" width={20} height={20} />
            </button>
          )}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => downloadAttachment(current.id)}
            className="flex items-center gap-8 px-12 py-8 rounded-base border-subtle hover:surface-elevated-more">
            <Icon name="download" width={20} height={20} />
            <span className="typo-text-14 text-full">{t('returns.download_image')}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ReturnImageGallery;
